import { normalizeCategoryAppliesTo, normalizeProductCategoryId } from '../domain/categoryTypes'
import type { CategoryAppliesTo, CategoryRecord } from '../domain/categoryTypes';
import { supabase } from '../../shared/supabase/supabaseClient';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { buildCatalogSyncOperationId } from '../../sync/core/catalogSyncOperationId';
import { type WebCatalogCategoryRow } from './catalogRamSnapshotCache';
import { resolveCategoryRowFromWebCache } from './webCatalogMutationResolve';
import type { CategoryRow } from './categoryRowTypes';
export type { CategoryRow } from './categoryRowTypes';
export const CATEGORIES_LAST_PULL_AT_KEY = 'sync.categories_last_pull_at';

export type CategoryMutationInput = {
  name: string;
  appliesTo?: CategoryAppliesTo;
  parentId?: string | null;
  code?: string | null;
  defaultMarginPercent?: number | null;
  description?: string | null;
  imageUrl?: string | null;
  isActive?: boolean;
  isRestricted?: boolean;
};

export type CategorySyncPayload = {
  organizationId: string;
  id: string;
  operationId: string;
  slug: string;
  name: string;
  isActive: boolean;
  isRestricted: boolean;
  sortOrder: number;
  color: string | null;
  appliesTo: CategoryAppliesTo;
  parentId: string | null;
  code: string | null;
  defaultMarginPercent: number | null;
  description: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export const mapWebCategoryRow = (row: WebCatalogCategoryRow): CategoryRecord => ({
  id: String(row.id),
  organizationId: row.organization_id ?? null,
  slug: String(row.slug ?? '').trim() || normalizeProductCategoryId(String(row.name ?? '')),
  name: String(row.name ?? '').trim(),
  isActive: row.is_active !== false,
  isRestricted: row.is_restricted === true,
  sortOrder: row.sort_order ?? 0,
  color: row.color ?? null,
  appliesTo: normalizeCategoryAppliesTo(row.applies_to),
  parentId: row.parent_id ?? null,
  code: row.code ?? null,
  defaultMarginPercent: row.default_margin_percent ?? null,
  description: row.description ?? null,
  imageUrl: row.image_url ?? null,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
  deletedAt: row.deleted_at ?? null,
});

export const mapRow = (row: CategoryRow): CategoryRecord => ({
  id: row.id,
  organizationId: row.organization_id ?? null,
  slug: row.slug,
  name: row.name,
  isActive: row.is_active === true || row.is_active === 1,
  isRestricted: row.is_restricted === true || row.is_restricted === 1,
  sortOrder: row.sort_order ?? 0,
  color: row.color ?? null,
  appliesTo: normalizeCategoryAppliesTo(row.applies_to),
  parentId: row.parent_id ?? null,
  code: row.code ?? null,
  defaultMarginPercent: row.default_margin_percent ?? null,
  description: row.description ?? null,
  imageUrl: row.image_url ?? null,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
  deletedAt: row.deleted_at ?? null,
});

export const normalizeCategoryName = (value: string) => value.trim().replace(/\s+/g, ' ');

export const slugifyCategoryName = (value: string) =>
  normalizeCategoryName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

export const buildUniqueCategorySlug = async (name: string, _organizationId: string | null, _excludedCategoryId?: string) => {
  // ADR-017 Fase 4: SQLite local eliminado — usar slug canónico sin dedup local.
  return slugifyCategoryName(name) || 'categoria';
};

/** Slugs canónicos: no se inventan nombres. */
export const buildUniqueCategorySlugRemote = async (name: string, _organizationId: string) => {
  return slugifyCategoryName(name) || 'categoria';
};

export const toSyncPayload = (category: CategoryRecord, organizationId: string): CategorySyncPayload => {
  const updatedAt = category.updatedAt ?? new Date().toISOString();
  return {
    organizationId,
    id: category.id,
    // operationId por COMANDO (id + versión), no por entidad: si fuera solo el id, el servidor
    // reclamaría la clave en el alta y descartaría toda edición posterior como idempotente.
    operationId: buildCatalogSyncOperationId(category.id, { updatedAt }),
    slug: category.slug,
    name: category.name,
    isActive: category.isActive,
    isRestricted: category.isRestricted === true,
    sortOrder: category.sortOrder,
    color: category.color ?? null,
    appliesTo: category.appliesTo,
    parentId: category.parentId ?? null,
    code: category.code ?? null,
    defaultMarginPercent: category.defaultMarginPercent ?? null,
    description: category.description ?? null,
    imageUrl: category.imageUrl ?? null,
    createdAt: category.createdAt ?? new Date().toISOString(),
    updatedAt,
    deletedAt: category.deletedAt ?? null,
  };
};

export const resolveOrganizationId = async () => {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) {
    throw new Error('No pudimos identificar la organización activa.');
  }
  return organizationId;
};

export const upsertLocalCategories = async (_categories: CategoryRecord[]) => {
  // ADR-017 Fase 4: SQLite local eliminado — el snapshot hidrata caches RAM, no SQLite.
};

export const CATEGORY_REMOTE_SELECT =
  'id,organization_id,slug,name,is_active,is_restricted,sort_order,color,applies_to,parent_id,code,default_margin_percent,description,image_url,created_at,updated_at,deleted_at';

export const CATEGORY_ROW_SELECT = `id, organization_id, slug, name, is_active, is_restricted, sort_order, color, applies_to, parent_id, code, default_margin_percent, description, image_url, created_at, updated_at, deleted_at`;

export const getCategoryById = async (categoryId: string): Promise<CategoryRow | null> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de Supabase.
  const { data, error } = await supabase
    .from('categories')
    .select(CATEGORY_REMOTE_SELECT)
    .eq('id', categoryId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as CategoryRow) ?? null;
};

/** Incluye categorías dadas de baja (borrado lógico) para restaurar u otras mutaciones sobre filas eliminadas. */
export const getCategoryRowByIdIncludingDeleted = async (categoryId: string): Promise<CategoryRow | null> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de Supabase.
  const organizationId = await getSessionOrganizationId();
  let query = supabase.from('categories').select(CATEGORY_REMOTE_SELECT).eq('id', categoryId);
  if (organizationId) query = query.eq('organization_id', organizationId);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return (data as CategoryRow) ?? null;
};

/** Fila para mutar: SQLite local o cache web ram-only. */
export const resolveCategoryRowForMutation = async (categoryId: string): Promise<CategoryRow | null> => {
  const organizationId = await getSessionOrganizationId();
        return resolveCategoryRowFromWebCache(categoryId, organizationId);
};
